import React, { useLayoutEffect, useRef, useState, useEffect, useCallback } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useSarLabAuth } from '../contexts/SarLabAuthContext';
import { bonusCodesApi, callMeApi, depositApi, withdrawApi, BankAccount } from '../services/api';
import {
  CalendarDays,
  Dices,
  MonitorPlay,
  Building2,
  Trophy,
  MoreHorizontal,
  UserPlus,
  LogIn,
  LogOut,
  User,
  Settings,
  ArrowUpRight,
  ArrowDownToLine,
  ArrowUpFromLine,
  Banknote,
  CreditCard,
  X,
  Gift,
  CheckCircle,
  AlertCircle,
  Wallet,
  Phone
} from 'lucide-react';
import './CardNav.css';
import LanguageSelector from './LanguageSelector';


const CardNav: React.FC = () => {
  const { t } = useTranslation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [showYatirimModal, setShowYatirimModal] = useState(false);
  const [showCekimModal, setShowCekimModal] = useState(false);
  const [showBonusKodModal, setShowBonusKodModal] = useState(false);
  const [showCallMeModal, setShowCallMeModal] = useState(false);
  const [callMeStatus, setCallMeStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [callMeMessage, setCallMeMessage] = useState('');
  const [bonusKod, setBonusKod] = useState('');
  const [bonusKodStatus, setBonusKodStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [bonusKodMessage, setBonusKodMessage] = useState('');

  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loadingMethods, setLoadingMethods] = useState(false);

  const [yatirimForm, setYatirimForm] = useState({ bankAccountId: '', miktar: '' });
  const [yatirimStatus, setYatirimStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [yatirimMessage, setYatirimMessage] = useState('');
  const [yatirimPaymentInfo, setYatirimPaymentInfo] = useState<{
    bankName: string;
    accountHolder: string;
    iban: string;
  } | null>(null);

  const [cekimForm, setCekimForm] = useState({ gatewayCode: 'havale', iban: '', isimSoyisim: '', miktar: '' });
  const [cekimStatus, setCekimStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [cekimMessage, setCekimMessage] = useState('');

  const navRef = useRef<HTMLDivElement>(null);
  const cardsRef = useRef<HTMLDivElement[]>([]);
  const location = useLocation();
  const { currentPlayer, wallet, logout, refreshPlayerData } = useSarLabAuth();

  // SarLab'da admin kontrolü - şimdilik placeholder
  const isAdmin = false; // TODO: SarLab backend'den admin rolü kontrolü eklenecek

  const loadPaymentMethods = useCallback(async () => {
    if (!currentPlayer) return;
    setLoadingMethods(true);
    try {
      const result = await depositApi.getMethods();
      if (result.success && result.data) {
        setBankAccounts(result.data.bankAccounts || []);
      }
    } catch (error) {
      // Silently fail
    } finally {
      setLoadingMethods(false);
    }
  }, [currentPlayer]);

  useEffect(() => {
    if (showYatirimModal || showCekimModal) {
      loadPaymentMethods();
    }
  }, [showYatirimModal, showCekimModal, loadPaymentMethods]);

  const handleYatirimSubmit = async () => {
    if (!currentPlayer || !yatirimForm.bankAccountId || !yatirimForm.miktar) return;

    const amountMinor = Math.round(parseFloat(yatirimForm.miktar) * 100);
    if (isNaN(amountMinor) || amountMinor <= 0) {
      setYatirimStatus('error');
      setYatirimMessage(t('navigation:deposit.invalidAmount'));
      return;
    }

    setYatirimStatus('loading');
    setYatirimMessage('');

    try {
      const result = await depositApi.create({
        gatewayCode: 'havale',
        amountMinor,
        currency: 'TRY',
        bankAccountId: yatirimForm.bankAccountId,
      });

      if (result.success && result.data) {
        setYatirimStatus('success');
        setYatirimMessage(t('navigation:deposit.orderCreated'));
        if (result.data.paymentInfo?.bankAccount) {
          setYatirimPaymentInfo(result.data.paymentInfo.bankAccount);
        }
      } else {
        setYatirimStatus('error');
        setYatirimMessage(result.error || t('navigation:deposit.error'));
      }
    } catch (error) {
      setYatirimStatus('error');
      setYatirimMessage(t('navigation:deposit.error'));
    }
  };

  const handleCekimSubmit = async () => {
    if (!currentPlayer || !cekimForm.iban || !cekimForm.isimSoyisim || !cekimForm.miktar) return;

    const amountMinor = Math.round(parseFloat(cekimForm.miktar) * 100);
    if (isNaN(amountMinor) || amountMinor <= 0) {
      setCekimStatus('error');
      setCekimMessage(t('navigation:withdraw.invalidAmount'));
      return;
    }

    if (wallet && amountMinor > wallet.balance * 100) {
      setCekimStatus('error');
      setCekimMessage(t('navigation:withdraw.insufficientBalance'));
      return;
    }

    setCekimStatus('loading');
    setCekimMessage('');

    try {
      const result = await withdrawApi.create({
        gatewayCode: cekimForm.gatewayCode,
        amountMinor,
        currency: 'TRY',
        accountInfo: {
          iban: cekimForm.iban.replace(/\s/g, ''),
          accountHolder: cekimForm.isimSoyisim,
        },
      });

      if (result.success && result.data) {
        setCekimStatus('success');
        setCekimMessage(t('navigation:withdraw.requestReceived'));
        refreshPlayerData();
      } else {
        setCekimStatus('error');
        setCekimMessage(result.error || t('navigation:withdraw.error'));
      }
    } catch (error) {
      setCekimStatus('error');
      setCekimMessage(t('navigation:withdraw.error'));
    }
  };

  const closeMenu = useCallback(() => {
    setIsMenuOpen(false);
    if (navRef.current) {
      navRef.current.style.height = '60px';
      
      // Reverse animation for cards
      cardsRef.current.forEach((card, index) => {
        if (card) {
          setTimeout(() => {
            card.style.transform = 'translateY(50px)';
            card.style.opacity = '0';
          }, index * 20);
        }
      });
    }
  }, []);

  // Beni Ara fonksiyonu - SarLab API entegrasyonu
  const handleCallMe = async () => {
    if (!currentPlayer) return;

    setCallMeStatus('loading');
    setCallMeMessage('');

    try {
      const result = await callMeApi.request();

      if (result.success && result.data) {
        setCallMeStatus('success');
        setCallMeMessage(result.data.message || t('navigation:callMe.success'));
      } else {
        setCallMeStatus('error');
        setCallMeMessage(result.error || t('navigation:callMe.error'));
      }
    } catch (error) {
      setCallMeStatus('error');
      setCallMeMessage(t('navigation:callMe.error'));
    }
  };

  // Bonus kod kullanım fonksiyonu
  const handleBonusKodKullan = async () => {
    if (!currentPlayer || !bonusKod.trim()) return;

    setBonusKodStatus('loading');
    setBonusKodMessage('');

    try {
      const result = await bonusCodesApi.redeem(bonusKod.trim().toUpperCase());

      if (result.success && result.data) {
        setBonusKodStatus('success');
        setBonusKodMessage(result.data.message || t('navigation:bonusCode.success'));
        setBonusKod('');
      } else {
        setBonusKodStatus('error');
        setBonusKodMessage(result.error || t('navigation:bonusCode.invalid'));
      }
    } catch (error) {
      setBonusKodStatus('error');
      setBonusKodMessage(t('navigation:bonusCode.error'));
    }
  };

  // Sayfa değişiminde menüyü kapat
  useEffect(() => {
    // Hemen menüyü kapat
    closeMenu();
    
    // Ek güvenlik için timeout ile de kapat
    const timeoutId = setTimeout(() => {
      closeMenu();
    }, 50);
    
    return () => clearTimeout(timeoutId);
  }, [location.pathname, closeMenu]);

  // Menü durumuna göre tabIndex kontrolü
  useEffect(() => {
    const contentElement = document.querySelector('.card-nav-content');
    if (contentElement) {
      const links = contentElement.querySelectorAll('a');
      links.forEach(link => {
        if (isMenuOpen) {
          link.removeAttribute('tabIndex');
        } else {
          link.setAttribute('tabIndex', '-1');
        }
      });
    }
  }, [isMenuOpen]);

  const allNavItems = [
    { path: '/etkinlikler', label: t('navigation:cardNav.events'), icon: CalendarDays },
    { path: '/slotlar', label: t('navigation:cardNav.slots'), icon: Dices },
    { path: '/canli-casino', label: t('navigation:cardNav.liveCasino'), icon: MonitorPlay },
    { path: '/sponsors', label: t('navigation:cardNav.sponsors'), icon: Building2 },
    { path: '/spor-bahisleri', label: t('navigation:cardNav.sportsBetting'), icon: Trophy },
    { path: '/diger', label: t('navigation:cardNav.other'), icon: MoreHorizontal }
  ];

  const calculateHeight = () => {
    const navEl = navRef.current;
    if (!navEl) return 700; // Even more increased default height

    const isMobile = window.matchMedia('(max-width: 768px)').matches;
    if (isMobile) {
      // Mobilde sabit yükseklik kullan (scroll için)
      return 80; // 60px top bar + 20px padding
    }
    return 700; // Even more increased default height
  };

  useLayoutEffect(() => {
    const navEl = navRef.current;
    if (!navEl) return;

    // Initial state
    navEl.style.height = '60px';
    navEl.style.overflow = 'hidden';
    
    cardsRef.current.forEach(card => {
      if (card) {
        card.style.transform = 'translateY(50px)';
        card.style.opacity = '0';
        card.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
      }
    });
  }, []);

  useLayoutEffect(() => {
    const handleResize = () => {
      if (isMenuOpen) {
        const newHeight = calculateHeight();
        if (navRef.current) {
          navRef.current.style.height = `${newHeight}px`;
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isMenuOpen]);

  // Dışarı tıklandığında kapatma
  useLayoutEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        if (isMenuOpen) {
          closeMenu();
        }
      }
    };

    if (isMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMenuOpen, closeMenu]);

  const openMenu = () => {
    setIsMenuOpen(true);
    if (navRef.current) {
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (isMobile) {
        navRef.current.style.height = '90vh';
      } else {
        const height = calculateHeight();
        navRef.current.style.height = `${height}px`;
      }
      
      // Staggered animation for cards
      cardsRef.current.forEach((card, index) => {
        if (card) {
          setTimeout(() => {
            card.style.transform = 'translateY(0)';
            card.style.opacity = '1';
          }, index * 80);
        }
      });
    }
  };

  const toggleMenu = () => {
    if (!isMenuOpen) {
      openMenu();
    } else {
      closeMenu();
    }
  };

  const setCardRef = (i: number) => (el: HTMLDivElement | null) => {
    if (el) cardsRef.current[i] = el;
  };

  const getCardLinks = (category: string) => {
    switch (category) {
      case t('navigation:cardNav.events'):
        return [
          { label: t('navigation:cardNav.tournaments'), href: '/etkinlikler/turnuvalar', icon: ArrowUpRight },
          { label: t('navigation:cardNav.promotions'), href: '/etkinlikler/promosyonlar', icon: ArrowUpRight }
        ];
      case t('navigation:cardNav.slots'):
        return [
          { label: t('navigation:cardNav.lobby'), href: '/slotlar/lobby', icon: ArrowUpRight },
          { label: t('navigation:cardNav.popularGames'), href: '/slotlar/populer', icon: ArrowUpRight },
          { label: t('navigation:cardNav.newReleases'), href: '/slotlar/yeni', icon: ArrowUpRight },
          { label: t('navigation:cardNav.freeSpins'), href: '/slotlar/free-spin', icon: ArrowUpRight }
        ];
      case t('navigation:cardNav.liveCasino'):
        return [
          { label: t('navigation:cardNav.liveLobby'), href: '/canli-casino/lobi', icon: ArrowUpRight },
          { label: t('navigation:cardNav.tableGames'), href: '/canli-casino/masa-oyunlari', icon: ArrowUpRight },
          { label: t('navigation:cardNav.roulette'), href: '/canli-casino/rulet', icon: ArrowUpRight },
          { label: t('navigation:cardNav.blackjack'), href: '/canli-casino/blackjack', icon: ArrowUpRight }
        ];
      case t('navigation:cardNav.sponsors'):
        return [
          { label: t('navigation:cardNav.sponsors'), href: '/sponsors', icon: ArrowUpRight },
          { label: t('navigation:cardNav.becomeaSponsor'), href: '/sponsors/apply', icon: ArrowUpRight }
        ];
      case t('navigation:cardNav.sportsBetting'):
        return [
          { label: t('navigation:cardNav.live'), href: '/spor-bahisleri/canli', icon: ArrowUpRight },
          { label: t('navigation:cardNav.upcoming'), href: '/spor-bahisleri/yaklasan', icon: ArrowUpRight },
          { label: t('navigation:cardNav.football'), href: '/spor-bahisleri/futbol', icon: ArrowUpRight },
          { label: t('navigation:cardNav.basketball'), href: '/spor-bahisleri/basketbol', icon: ArrowUpRight }
        ];
      case t('navigation:cardNav.other'):
        return [
          { label: t('navigation:cardNav.vpn'), href: '/diger/vpn', icon: ArrowUpRight },
          { label: t('navigation:cardNav.blog'), href: '/diger/blog', icon: ArrowUpRight },
          { label: t('navigation:cardNav.callMe'), href: '#call-me', icon: Phone, isCallMe: true },
          { label: t('navigation:cardNav.bonusCode'), href: '#bonus-kod', icon: Gift, isButton: true }
        ];
      default:
        return [];
    }
  };

  return (
    <>
      <div className={`card-nav-overlay ${isMenuOpen ? 'open' : ''}`} onClick={() => {
        if (isMenuOpen) {
          closeMenu();
          if (navRef.current) {
            navRef.current.style.height = '60px';
            
            // Reverse animation for cards
            cardsRef.current.forEach((card, index) => {
              if (card) {
                setTimeout(() => {
                  card.style.transform = 'translateY(50px)';
                  card.style.opacity = '0';
                }, index * 40);
              }
            });
          }
        }
      }} />
      <div className="card-nav-container">
        <nav ref={navRef} className={`card-nav ${isMenuOpen ? 'open' : ''}`}>
        <div className="card-nav-top">
        <div
          className={`hamburger-menu ${isMenuOpen ? 'open' : ''}`}
          onClick={toggleMenu}
          role="button"
          aria-label={isMenuOpen ? t('navigation:cardNav.closeMenu') : t('navigation:cardNav.openMenu')}
          tabIndex={0}
        >
            <div className="hamburger-line" />
            <div className="hamburger-line" />
          </div>

                 <Link to="/" className="logo-container" onClick={() => closeMenu()}>
                   <span className="logo-text jojobet-logo">
                     <span className="JojoBet-text">JojoBet</span>
                     <span className="bet-text"></span>
                   </span>
                 </Link>

          {currentPlayer ? (
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <div className="hidden sm:flex items-center gap-1.5 px-3 rounded-lg card-nav-cta-button" style={{ marginRight: 0, background: 'rgba(0, 0, 0, 0.7)', borderColor: 'black', height:'22px'}}>
                <Wallet className="w-4 h-4 text-green-400" />
                <span className="text-sm font-semibold text-cyan-400">
                  {(wallet?.balance || 0).toLocaleString('tr-TR')} ₺
                </span>
              </div>
              <Link
                to="/profile"
                className="card-nav-cta-button"
                onClick={() => {
                  closeMenu();
                }}
              >
                <User className="w-4 h-4 mr-2" />
                {currentPlayer?.username || t('navigation:cardNav.user')}
              </Link>
            </div>
          ) : (
            <div className="flex items-center gap-2">
              <LanguageSelector />
              <Link
                to="/login"
                className="card-nav-cta-button"
                onClick={() => {
                  closeMenu();
                }}
              >
                <LogIn className="w-4 h-4 mr-2" />
                {t('navigation:cardNav.login')}
              </Link>
            </div>
          )}
        </div>

        <div
          className={`card-nav-content ${!isMenuOpen ? 'inert' : ''}`}
          // @ts-ignore - inert is a valid HTML attribute but not typed in React yet
          inert={!isMenuOpen ? true : undefined}
        >
          {allNavItems.map((item, idx) => (
            <div
              key={`${item.label}-${idx}`}
              className="nav-card"
              ref={setCardRef(idx)}
            >
              <div className="nav-card-label">{item.label}</div>
              <div className="nav-card-links">
                {getCardLinks(item.label).map((link: any, i) => (
                  link.isCallMe ? (
                    <button
                      key={`${link.label}-${i}`}
                      className="nav-card-link"
                      onClick={() => {
                        closeMenu();
                        if (currentPlayer) {
                          setShowCallMeModal(true);
                        } else {
                          window.location.href = '/login';
                        }
                      }}
                    >
                      <link.icon className="nav-card-link-icon" aria-hidden="true" />
                      {link.label}
                    </button>
                  ) : link.isButton ? (
                    <button
                      key={`${link.label}-${i}`}
                      className="nav-card-link"
                      onClick={() => {
                        closeMenu();
                        if (currentPlayer) {
                          setShowBonusKodModal(true);
                        } else {
                          // Giriş yapmamış kullanıcılar için login'e yönlendir
                          window.location.href = '/login';
                        }
                      }}
                    >
                      <link.icon className="nav-card-link-icon" aria-hidden="true" />
                      {link.label}
                    </button>
                  ) : (
                    <Link
                      key={`${link.label}-${i}`}
                      className="nav-card-link"
                      to={link.href}
                      onClick={() => {
                        // Hemen menüyü kapat
                        closeMenu();

                        // Ek güvenlik için timeout ile de kapat
                        setTimeout(() => {
                          closeMenu();
                        }, 100);
                      }}
                    >
                      <link.icon className="nav-card-link-icon" aria-hidden="true" />
                      {link.label}
                    </Link>
                  )
                ))}
              </div>
        </div>
      ))}
      
                 {/* Alt çizgi ve Kimlik doğrulama butonları */}
                 <div className="auth-section-mobile">
                   <div className="auth-divider"></div>
                   {currentPlayer ? (
                     // Kullanıcı giriş yapmış
                     <div className="auth-buttons-container">
                       {isAdmin && (
                         <>
                           <Link
                             to="/admin"
                             className="auth-button-mobile admin-button"
                             onClick={() => {
                               closeMenu();
                             }}
                           >
                             <Settings className="w-5 h-5" />
                             {t('navigation:cardNav.admin')}
                           </Link>
                           <div className="auth-button-divider"></div>
                         </>
                       )}
                       <div className="logout-yatirim-cekim-row">
                         <button
                           className="auth-button-mobile logout-button"
                           style={{ width: '70%' }}
                           onClick={() => {
                             logout();
                             closeMenu();
                           }}
                         >
                           <LogOut className="w-5 h-5" />
                           {t('navigation:cardNav.logout')}
                         </button>
                         <button
                           className="auth-button-mobile yatirim-button"
                           style={{ width: '15%' }}
                           onClick={() => {
                             setShowYatirimModal(true);
                             closeMenu();
                           }}
                           title={t('navigation:cardNav.deposit')}
                         >
                           <ArrowDownToLine className="w-5 h-5" />
                         </button>
                         <button
                           className="auth-button-mobile cekim-button"
                           style={{ width: '15%' }}
                           onClick={() => {
                             setShowCekimModal(true);
                             closeMenu();
                           }}
                           title={t('navigation:cardNav.withdraw')}
                         >
                           <ArrowUpFromLine className="w-5 h-5" />
                         </button>
                       </div>
                     </div>
                   ) : (
                     // Kullanıcı giriş yapmamış
                     <div className="auth-buttons-container">
                       <Link
                         to="/login"
                         className="auth-button-mobile login-button"
                         onClick={() => {
                           closeMenu();
                         }}
                       >
                         <LogIn className="w-5 h-5" />
                         {t('navigation:cardNav.login')}
                       </Link>
                       <div className="auth-button-divider"></div>
                       <Link
                         to="/register"
                         className="auth-button-mobile register-button"
                         onClick={() => {
                           closeMenu();
                         }}
                       >
                         <UserPlus className="w-5 h-5" />
                         {t('navigation:cardNav.register')}
                       </Link>
                     </div>
                   )}
                 </div>
    </div>
        </nav>
      </div>

      {/* Yatırım Modal */}
      <AnimatePresence>
        {showYatirimModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => {
              setShowYatirimModal(false);
              setYatirimStatus('idle');
              setYatirimMessage('');
              setYatirimPaymentInfo(null);
              setYatirimForm({ bankAccountId: '', miktar: '' });
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-2xl p-6 w-full max-w-md border border-dark-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-green-500 to-emerald-500 flex items-center justify-center">
                    <Banknote className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{t('navigation:deposit.title')}</h3>
                </div>
                <button
                  onClick={() => {
                    setShowYatirimModal(false);
                    setYatirimStatus('idle');
                    setYatirimMessage('');
                    setYatirimPaymentInfo(null);
                    setYatirimForm({ bankAccountId: '', miktar: '' });
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {yatirimStatus === 'success' && yatirimPaymentInfo ? (
                <div className="space-y-4">
                  <div className="flex flex-col items-center py-4">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-green-400 text-center mb-4">{yatirimMessage}</p>
                  </div>

                  <div className="bg-dark-700/50 rounded-lg p-4 space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">{t('navigation:deposit.bank')}</span>
                      <span className="text-white font-semibold">{yatirimPaymentInfo.bankName}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">{t('navigation:deposit.accountHolder')}</span>
                      <span className="text-white font-semibold">{yatirimPaymentInfo.accountHolder}</span>
                    </div>
                    <div className="flex flex-col">
                      <span className="text-gray-400 text-sm mb-1">{t('navigation:deposit.iban')}</span>
                      <span className="text-white font-mono text-sm break-all">{yatirimPaymentInfo.iban}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400 text-sm">{t('navigation:deposit.amount2')}</span>
                      <span className="text-green-400 font-bold">{yatirimForm.miktar} ₺</span>
                    </div>
                  </div>

                  <p className="text-xs text-gray-500 text-center">
                    {t('navigation:deposit.transferNote')}
                  </p>

                  <button
                    onClick={() => {
                      setShowYatirimModal(false);
                      setYatirimStatus('idle');
                      setYatirimMessage('');
                      setYatirimPaymentInfo(null);
                      setYatirimForm({ bankAccountId: '', miktar: '' });
                    }}
                    className="w-full bg-dark-700 text-white font-semibold py-3 rounded-lg hover:bg-dark-600 transition-colors"
                  >
                    {t('navigation:deposit.ok')}
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  {loadingMethods ? (
                    <div className="flex items-center justify-center py-8">
                      <svg className="animate-spin h-8 w-8 text-green-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                    </div>
                  ) : (
                    <>
                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Banka Hesabı Seçin</label>
                        <select
                          value={yatirimForm.bankAccountId}
                          onChange={(e) => setYatirimForm({ ...yatirimForm, bankAccountId: e.target.value })}
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white focus:outline-none focus:border-primary-500"
                          disabled={yatirimStatus === 'loading'}
                        >
                          <option value="">Banka Seçin</option>
                          {bankAccounts.map((bank) => (
                            <option key={bank.id} value={bank.id}>
                              {bank.bankName} - {bank.accountHolder}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-300 mb-2">Miktar (₺)</label>
                        <input
                          type="number"
                          value={yatirimForm.miktar}
                          onChange={(e) => setYatirimForm({ ...yatirimForm, miktar: e.target.value })}
                          placeholder="0.00"
                          min="0"
                          step="0.01"
                          className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                          disabled={yatirimStatus === 'loading'}
                        />
                      </div>

                      {yatirimMessage && (
                        <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                          yatirimStatus === 'error'
                            ? 'bg-red-500/20 text-red-400'
                            : 'bg-green-500/20 text-green-400'
                        }`}>
                          {yatirimStatus === 'error' ? (
                            <AlertCircle className="w-5 h-5 flex-shrink-0" />
                          ) : (
                            <CheckCircle className="w-5 h-5 flex-shrink-0" />
                          )}
                          <span className="text-sm">{yatirimMessage}</span>
                        </div>
                      )}

                      {wallet && (
                        <div className="bg-dark-700/50 rounded-lg p-3 flex items-center justify-between">
                          <span className="text-gray-400 text-sm">Mevcut Bakiye:</span>
                          <span className="text-white font-bold">{(wallet.balance || 0).toLocaleString('tr-TR')} ₺</span>
                        </div>
                      )}

                      <button
                        onClick={handleYatirimSubmit}
                        disabled={yatirimStatus === 'loading' || !yatirimForm.bankAccountId || !yatirimForm.miktar}
                        className="w-full bg-gradient-to-r from-green-500 to-emerald-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                      >
                        {yatirimStatus === 'loading' ? (
                          <>
                            <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                            </svg>
                            İşleniyor...
                          </>
                        ) : (
                          'Emir Ver'
                        )}
                      </button>
                    </>
                  )}
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Çekim Modal */}
      <AnimatePresence>
        {showCekimModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => {
              setShowCekimModal(false);
              setCekimStatus('idle');
              setCekimMessage('');
              setCekimForm({ gatewayCode: 'havale', iban: '', isimSoyisim: '', miktar: '' });
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-2xl p-6 w-full max-w-md border border-dark-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-orange-500 to-red-500 flex items-center justify-center">
                    <CreditCard className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Çekim Yap</h3>
                </div>
                <button
                  onClick={() => {
                    setShowCekimModal(false);
                    setCekimStatus('idle');
                    setCekimMessage('');
                    setCekimForm({ gatewayCode: 'havale', iban: '', isimSoyisim: '', miktar: '' });
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              {cekimStatus === 'success' ? (
                <div className="space-y-4">
                  <div className="flex flex-col items-center py-4">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <p className="text-green-400 text-center">{cekimMessage}</p>
                  </div>

                  <button
                    onClick={() => {
                      setShowCekimModal(false);
                      setCekimStatus('idle');
                      setCekimMessage('');
                      setCekimForm({ gatewayCode: 'havale', iban: '', isimSoyisim: '', miktar: '' });
                    }}
                    className="w-full bg-dark-700 text-white font-semibold py-3 rounded-lg hover:bg-dark-600 transition-colors"
                  >
                    Tamam
                  </button>
                </div>
              ) : (
                <div className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">IBAN Numarası</label>
                    <input
                      type="text"
                      value={cekimForm.iban}
                      onChange={(e) => setCekimForm({ ...cekimForm, iban: e.target.value })}
                      placeholder="TR00 0000 0000 0000 0000 0000 00"
                      className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                      disabled={cekimStatus === 'loading'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Hesap Sahibi</label>
                    <input
                      type="text"
                      value={cekimForm.isimSoyisim}
                      onChange={(e) => setCekimForm({ ...cekimForm, isimSoyisim: e.target.value })}
                      placeholder="Ad Soyad"
                      className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                      disabled={cekimStatus === 'loading'}
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-300 mb-2">Miktar (₺)</label>
                    <input
                      type="number"
                      value={cekimForm.miktar}
                      onChange={(e) => setCekimForm({ ...cekimForm, miktar: e.target.value })}
                      placeholder="0.00"
                      min="0"
                      step="0.01"
                      className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500"
                      disabled={cekimStatus === 'loading'}
                    />
                  </div>

                  {cekimMessage && (
                    <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                      cekimStatus === 'error'
                        ? 'bg-red-500/20 text-red-400'
                        : 'bg-green-500/20 text-green-400'
                    }`}>
                      {cekimStatus === 'error' ? (
                        <AlertCircle className="w-5 h-5 flex-shrink-0" />
                      ) : (
                        <CheckCircle className="w-5 h-5 flex-shrink-0" />
                      )}
                      <span className="text-sm">{cekimMessage}</span>
                    </div>
                  )}

                  {wallet && (
                    <div className="bg-dark-700/50 rounded-lg p-3 flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Çekilebilir Bakiye:</span>
                      <span className="text-white font-bold">{(wallet.balance || 0).toLocaleString('tr-TR')} ₺</span>
                    </div>
                  )}

                  <button
                    onClick={handleCekimSubmit}
                    disabled={cekimStatus === 'loading' || !cekimForm.iban || !cekimForm.isimSoyisim || !cekimForm.miktar}
                    className="w-full bg-gradient-to-r from-orange-500 to-red-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                  >
                    {cekimStatus === 'loading' ? (
                      <>
                        <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                        </svg>
                        İşleniyor...
                      </>
                    ) : (
                      'Çekim Talebi Gönder'
                    )}
                  </button>

                  <p className="text-xs text-gray-500 text-center">
                    Çekim talepleri 1-24 saat içinde işleme alınır.
                  </p>
                </div>
              )}
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Bonus Kod Modal */}
      <AnimatePresence>
        {showBonusKodModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => {
              setShowBonusKodModal(false);
              setBonusKod('');
              setBonusKodStatus('idle');
              setBonusKodMessage('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-2xl p-6 w-full max-w-md border border-dark-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-purple-500 to-pink-500 flex items-center justify-center">
                    <Gift className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Bonus Kod Kullan</h3>
                </div>
                <button
                  onClick={() => {
                    setShowBonusKodModal(false);
                    setBonusKod('');
                    setBonusKodStatus('idle');
                    setBonusKodMessage('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-300 mb-2">Bonus Kodunuz</label>
                  <input
                    type="text"
                    value={bonusKod}
                    onChange={(e) => setBonusKod(e.target.value.toUpperCase())}
                    placeholder="Örn: jojobet750"
                    className="w-full bg-dark-700 border border-dark-600 rounded-lg px-4 py-3 text-white placeholder-gray-500 focus:outline-none focus:border-primary-500 uppercase"
                    disabled={bonusKodStatus === 'loading'}
                  />
                </div>

                {/* Status Messages */}
                {bonusKodMessage && (
                  <div className={`flex items-center space-x-2 p-3 rounded-lg ${
                    bonusKodStatus === 'success'
                      ? 'bg-green-500/20 text-green-400'
                      : 'bg-red-500/20 text-red-400'
                  }`}>
                    {bonusKodStatus === 'success' ? (
                      <CheckCircle className="w-5 h-5 flex-shrink-0" />
                    ) : (
                      <AlertCircle className="w-5 h-5 flex-shrink-0" />
                    )}
                    <span className="text-sm">{bonusKodMessage}</span>
                  </div>
                )}

                {/* Current Balance */}
                {wallet && (
                  <div className="bg-dark-700/50 rounded-lg p-3 flex items-center justify-between">
                    <span className="text-gray-400 text-sm">Mevcut Bakiye:</span>
                    <span className="text-white font-bold">{wallet.balance || 0} TL</span>
                  </div>
                )}

                <button
                  onClick={handleBonusKodKullan}
                  disabled={bonusKodStatus === 'loading' || !bonusKod.trim()}
                  className="w-full bg-gradient-to-r from-purple-500 to-pink-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {bonusKodStatus === 'loading' ? (
                    <>
                      <svg className="animate-spin -ml-1 mr-2 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                        <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                        <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                      </svg>
                      Kontrol Ediliyor...
                    </>
                  ) : (
                    'Kodu Kullan'
                  )}
                </button>

                <p className="text-xs text-gray-500 text-center">
                  Bonus kodları tek kullanımlıktır ve her hesap için bir kez kullanılabilir.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Beni Ara Modal */}
      <AnimatePresence>
        {showCallMeModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 backdrop-blur-sm z-[9999] flex items-center justify-center p-4"
            onClick={() => {
              setShowCallMeModal(false);
              setCallMeStatus('idle');
              setCallMeMessage('');
            }}
          >
            <motion.div
              initial={{ scale: 0.9, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.9, opacity: 0 }}
              className="bg-dark-800 rounded-2xl p-6 w-full max-w-md border border-dark-700"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between mb-6">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 rounded-lg bg-gradient-to-r from-cyan-500 to-blue-500 flex items-center justify-center">
                    <Phone className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-white">Beni Ara</h3>
                </div>
                <button
                  onClick={() => {
                    setShowCallMeModal(false);
                    setCallMeStatus('idle');
                    setCallMeMessage('');
                  }}
                  className="text-gray-400 hover:text-white transition-colors"
                >
                  <X className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                {callMeStatus === 'idle' && (
                  <>
                    <p className="text-gray-300 text-center">
                      Aranmak ister misiniz? Müşteri temsilcilerimiz en kısa sürede sizinle iletişime geçecektir.
                    </p>

                    <div className="bg-dark-700/50 rounded-lg p-3 flex items-center justify-between">
                      <span className="text-gray-400 text-sm">Kullanıcı:</span>
                      <span className="text-white font-semibold">{currentPlayer?.username}</span>
                    </div>

                    <button
                      onClick={handleCallMe}
                      className="w-full bg-gradient-to-r from-cyan-500 to-blue-500 text-white font-semibold py-3 rounded-lg hover:opacity-90 transition-opacity flex items-center justify-center"
                    >
                      <Phone className="w-5 h-5 mr-2" />
                      Beni Arayın
                    </button>
                  </>
                )}

                {callMeStatus === 'loading' && (
                  <div className="flex flex-col items-center py-4">
                    <svg className="animate-spin h-10 w-10 text-cyan-500 mb-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-gray-300">Talebiniz gönderiliyor...</span>
                  </div>
                )}

                {callMeStatus === 'success' && (
                  <div className="flex flex-col items-center py-4">
                    <div className="w-16 h-16 rounded-full bg-green-500/20 flex items-center justify-center mb-4">
                      <CheckCircle className="w-8 h-8 text-green-400" />
                    </div>
                    <span className="text-green-400 text-center">{callMeMessage}</span>
                    <button
                      onClick={() => {
                        setShowCallMeModal(false);
                        setCallMeStatus('idle');
                        setCallMeMessage('');
                      }}
                      className="mt-4 px-6 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors"
                    >
                      Tamam
                    </button>
                  </div>
                )}

                {callMeStatus === 'error' && (
                  <div className="flex flex-col items-center py-4">
                    <div className="w-16 h-16 rounded-full bg-red-500/20 flex items-center justify-center mb-4">
                      <AlertCircle className="w-8 h-8 text-red-400" />
                    </div>
                    <span className="text-red-400 text-center">{callMeMessage}</span>
                    <button
                      onClick={() => setCallMeStatus('idle')}
                      className="mt-4 px-6 py-2 bg-dark-700 text-white rounded-lg hover:bg-dark-600 transition-colors"
                    >
                      Tekrar Dene
                    </button>
                  </div>
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default CardNav;
